import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

/**
 * The standardised phenotype record (migration 0003).
 *
 * Labels for the vocabulary come from the SERVER, not from the i18n
 * catalogues. These are mycological terms — "adnexed", "decurrent",
 * "fibrillose" — and a Hebrew-speaking mycologist must be able to correct one
 * without a code deploy. `phenotype_vocabulary` holds both languages as data
 * for exactly that reason; only the field LABELS are translated here.
 *
 * `ring_present` and `volva_present` are three-state, and the third state is
 * the point. A volva is buried: it is visible only if the base was dug up.
 * "Didn't check" and "there is none" are different claims about an Amanita,
 * so this renders three radio buttons, never a checkbox.
 */

const TEXT_SELECTS = [
  'odor', 'odor_intensity',
  'stipe_shape', 'stipe_cross_section', 'cap_surface', 'gill_attachment',
  'spore_print_colour',
];

export default function PhenotypeForm({ value, onChange, compact = false, colourChange }) {
  const { t, i18n } = useTranslation('field');
  const [vocab, setVocab] = useState({});
  const isHebrew = i18n.language === 'he';

  useEffect(() => {
    let cancelled = false;
    api.get('/phenotype-vocabulary')
      .then(r => { if (!cancelled) setVocab(r.data || {}); })
      .catch(() => { /* the form still works as free text */ });
    return () => { cancelled = true; };
  }, []);

  const label = opt => (isHebrew ? opt.label_he : opt.label_en) || opt.value;
  const set = (field, v) => onChange({ ...value, [field]: v });

  const Select = ({ field }) => (
    <label style={styles.field}>
      <span style={styles.label}>{t(`phenotype.${field}`)}</span>
      <select
        value={value[field] || ''}
        onChange={e => set(field, e.target.value || null)}
        title={t('tip.phenotype.select')}
        style={styles.input}
      >
        <option value="">—</option>
        {(vocab[field] || []).map(o => (
          <option key={o.value} value={o.value}>{label(o)}</option>
        ))}
      </select>
    </label>
  );

  /* Three states, and "not checked" is the DEFAULT. Defaulting to "no" would
   * silently assert an absence nobody verified. */
  const TriState = ({ field, typeField }) => {
    const current = value[field];
    const options = [
      { v: true, text: t('phenotype.yes'), tip: t('tip.phenotype.yes') },
      { v: false, text: t('phenotype.no'), tip: t('tip.phenotype.no') },
      { v: null, text: t('phenotype.notChecked'), tip: t('tip.phenotype.notChecked') },
    ];
    return (
      <div style={styles.field}>
        <span style={styles.label}>{t(`phenotype.${field}`)}</span>
        <div style={styles.triRow}>
          {options.map(o => (
            <button
              key={String(o.v)}
              type="button"
              onClick={() => set(field, o.v)}
              title={o.tip}
              style={{
                ...styles.triBtn,
                ...(current === o.v ? styles.triBtnOn : null),
              }}
            >
              {o.text}
            </button>
          ))}
        </div>
        {current === null && (
          <span style={styles.hint}>{t('phenotype.notCheckedHint')}</span>
        )}
        {current === true && (
          <select
            value={value[typeField] || ''}
            onChange={e => set(typeField, e.target.value || null)}
            title={t('tip.phenotype.typeSelect')}
            style={{ ...styles.input, marginTop: 8 }}
          >
            <option value="">{t(`phenotype.${typeField}`)} —</option>
            {(vocab[typeField] || []).map(o => (
              <option key={o.value} value={o.value}>{label(o)}</option>
            ))}
          </select>
        )}
      </div>
    );
  };

  return (
    <div style={compact ? styles.wrapCompact : styles.wrap}>
      {!compact && <p style={styles.help}>{t('phenotype.help')}</p>}

      {TEXT_SELECTS.slice(0, 2).map(f => <Select key={f} field={f} />)}

      <fieldset style={styles.group}>
        <legend style={styles.legend}>{t('phenotype.colourChange')}</legend>
        <Select field="colour_change_location" />
        <label style={styles.field}>
          <span style={styles.label}>{t('phenotype.colour_change_from')}</span>
          <input
            style={styles.input}
            value={value.colour_change_from || ''}
            onChange={e => set('colour_change_from', e.target.value)}
            title={t('tip.phenotype.colourFrom')}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.label}>{t('phenotype.colour_change_to')}</span>
          <input
            style={styles.input}
            value={value.colour_change_to || ''}
            onChange={e => set('colour_change_to', e.target.value)}
            title={t('tip.phenotype.colourTo')}
          />
        </label>
        <Select field="colour_change_delay" />
      </fieldset>

      <fieldset style={styles.group}>
        <legend style={styles.legend}>{t('phenotype.colours')}</legend>
        <Select field="cap_colour" />
        <Select field="stipe_colour" />
      </fieldset>

      {/* The flesh's reaction when cut or pressed. A reaction other than
          "none" is the change field_collection.colour_change flags, which also
          requires a cross-section photograph; say so rather than set it here. */}
      <fieldset style={styles.group} data-testid="phenotype-bruising">
        <legend style={styles.legend}>{t('phenotype.bruising')}</legend>
        <Select field="bruising_reaction" />
        <Select field="bruising_trigger" />
        {value.bruising_reaction && value.bruising_reaction !== 'none' && colourChange === false && (
          <span role="note" data-testid="phenotype-colour-change-hint" style={styles.hint}>
            {t('phenotype.bruisingColourChangeHint')}
          </span>
        )}
      </fieldset>

      {TEXT_SELECTS.slice(2).map(f => <Select key={f} field={f} />)}

      <TriState field="ring_present" typeField="ring_type" />
      <TriState field="volva_present" typeField="volva_type" />

      <label style={styles.field}>
        <span style={styles.label}>{t('phenotype.notes')}</span>
        <textarea
          rows={3}
          style={{ ...styles.input, resize: 'vertical' }}
          value={value.notes || ''}
          onChange={e => set('notes', e.target.value)}
          title={t('tip.phenotype.notes')}
        />
      </label>
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 14 },
  wrapCompact: { display: 'flex', flexDirection: 'column', gap: 10 },
  help: { margin: 0, color: 'var(--c-text-muted-3-fg)', fontSize: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-2-fg)' },
  input: {
    // 16px prevents iOS Safari from zooming the viewport on focus, which in
    // the field reads as the app jumping around under your thumb.
    fontSize: 16,
    padding: '11px 12px',
    border: '1px solid var(--c-border-strong-2)',
    borderRadius: 8,
    background: 'var(--c-surface)',
    font: 'inherit',
  },
  group: {
    border: '1px solid var(--c-border-3)',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    margin: 0,
  },
  legend: { fontSize: 13, fontWeight: 700, color: 'var(--c-text-body-2-fg)', padding: '0 6px' },
  triRow: { display: 'flex', gap: 8 },
  triBtn: {
    flex: 1,
    // 44px is the accepted minimum for a thumb; gloves make it a floor, not
    // a target.
    minHeight: 44,
    padding: '10px 8px',
    border: '1px solid var(--c-border-strong-2)',
    borderRadius: 8,
    background: 'var(--c-surface)',
    font: 'inherit',
    fontSize: 15,
    cursor: 'pointer',
  },
  triBtnOn: { background: 'var(--c-info-2)', color: 'var(--c-on-accent)', borderColor: 'var(--c-info-2)' },
  hint: { fontSize: 12, color: 'var(--c-warning-text-4-fg)', lineHeight: 1.4 },
};
